import { isArray, isObject, debugLog } from './Util';
import { LOGIN, HEARTBEAT, CLIENT_VERSION } from './actions/types';

// WebSocket utility class that handles common edge cases and provides a robust connection management system.
// 
// Key features:
// 
// Safe Message Handling:
// 
// Messages sent before connection are queued and sent once connected
// Automatic JSON parsing/stringifying of messages
// Error handling for malformed messages
// 
// 
// Auto-Reconnection:
// 
// Configurable delay between attempts
// Maintains message queue during reconnection
// Avoids connection idle timeout by sending a heartbeat message at a configurable interval
// 
// 
// Event Callbacks:
// 
// onMessage: Receives parsed message data
// onConnect: Called when connection is established
// onDisconnect: Called when connection is lost
// onError: Called for any errors (connection, parsing, sending)
// 
// 
// State Management:
// 
// Tracks connection state
// Manages reconnection 
// Handles message queue

function WebSocketUtility(url, options = {}) {
  // Validate params
  if(typeof url !== 'string') { throw new Error('non-string input url'); }

  if(url.match(/^wss:\/\/(?:(?:[\w-]+\.)*[\w-]+)(?::\d+)?(?:\/[\w-._~:/?#[\]@!$&'()*+,;=]*)?$/i) === null) { throw new Error('invalid URL'); }
  // Let me break down what this pattern matches:
  // 
  // ^wss:// - Must start with "wss://"
  // (?:[\w-]+\.)* - Zero or more subdomain segments followed by dots
  // [\w-]+ - The main domain name
  // (?::\d+)? - Optional port number (e.g., :8080)
  // (?:\/[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=]*)? - Optional path and query parameters
  // $ - End of string
  // i - Case insensitive flag

  if(!isObject(options)) { throw new Error('non-object options'); }

  var THIS = this;

  // Configuration
  THIS.url = url;
  THIS.options = {
    reconnectDelay: options.reconnectDelay || 3000,
    heartbeatDelay: options.heartbeatDelay || 1000*60*5, // five minutes
    optimisticDebounceDelay: 2000, // for optimistic updates, number of milliseconds to wait after user input has stopped to send update to server. To avoid excessive server usage
    onMessage: options.onMessage || (() => {}),
    onConnect: options.onConnect || (() => {}),
    onDisconnect: options.onDisconnect || (() => {}),
    onError: options.onError || (() => {})
  };

  // Public State
  THIS.ws = null;
  THIS.isConnected = false;
  THIS.autoReconnectEnabled = true;
  THIS.reconnectTimer = null;

  // Private State
  let isAuthorized = false; // have we logged in with the SiteBoard websocket server and received a success response?
  const loginMessageQueue = []; // login messages that were sent before the socket is connected
  const authorizedMessageQueue = []; // non-login messages that were sent before the socket has been authorized 
  const messageSuccessCallbacks = {}; // Callback functions for messages being tracked with a messageId. Called when we receive a message from the server with a matching messageId
  const messageErrorCallbacks = {}; // Callback functions for messages being tracked with a messageId. Called when we receive a message from the server with a matching messageId
  const messageTimeoutIds = {}; // Reference to the timer that will throw an error if a message doesn't receive a success response in preset amount of time
  const messageCallbackTimeout = 12000; // trigger an error if we don't receive a message response within 15 seconds
  const optimisticUpdateForId = {};

  let heartbeatTimer = null; // used to send a message periodically to keep the connection from idle timing out
  let lastHeartbeatSentTimestamp = new Date().getTime(); // used to keep track of if the websocket has gone stale due to the app being suspended in the background

  // TODO: implement websocket message callback timeouts
  // TODO: implement websocket message failed callback

  // Methods
  THIS.connect = function() {
    // TODO: maybe clear message queue if url changes, to avoid sending messages to the wrong url
    THIS.disconnect();

    try {
      THIS.autoReconnectEnabled = true;
      THIS.ws = new WebSocket(THIS.url);

      THIS.ws.onopen = () => {
        THIS.isConnected = true;
        THIS.options.onConnect();
        THIS.processLoginMessageQueue();
        heartbeatTimer = setInterval(() => {
          THIS.send({ 
            type: HEARTBEAT, 
            clientVersion: CLIENT_VERSION, // server will send a message with the latest client version if this is outdated
          }); 
          lastHeartbeatSentTimestamp = new Date().getTime();
        }, THIS.options.heartbeatDelay);
      };

      THIS.ws.onclose = () => {
        debugLog('WebSocket Closing at ' + new Date().toString());
        THIS.isConnected = false;
        THIS.setAuthorized(false);
        THIS.ws = null;
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
        THIS.options.onDisconnect();
        if(THIS.autoReconnectEnabled === true) {
          THIS.reconnect(); // Attempt reconnection
        }
      };

      THIS.ws.onerror = (error) => {
        THIS.options.onError(error);
      };

      THIS.ws.onmessage = (event) => {
        try {
          debugLog(event.data.length, event.data);
        } catch(error) {}

        // Attempt to match this message with one sent earlier and execute a callback function
        let messageHandled = false;
        try {
          const messageObject = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if(!isObject(messageObject)) throw new Error('');
          if(typeof messageObject.messageId !== 'string') throw new Error('');
          if(messageObject.messageId.length > 36) throw new Error('');
          if(messageObject.messageId.match(/^[a-fA-F0-9-_]+$/) === null) throw new Error('');

          // TODO: maybe remove error callback is we got a success callback and vice versa

          // dispatch success callback
          if(messageObject.status === 'success' && typeof messageSuccessCallbacks[messageObject.messageId] === 'function') {
            // Clear error timeout timer if it's set
            if(messageTimeoutIds[messageObject.messageId]) {
              clearTimeout(messageTimeoutIds[messageObject.messageId]);
              delete messageTimeoutIds[messageObject.messageId];
            }
            const callback = messageSuccessCallbacks[messageObject.messageId];
            delete messageSuccessCallbacks[messageObject.messageId];
            try { callback(messageObject) } catch(e) {}
            messageHandled = true;
          } 

          // dispatch error callback
          if(messageObject.status === 'error' && typeof messageErrorCallbacks[messageObject.messageId] === 'function') {
            // Clear error timeout timer if it's set
            if(messageTimeoutIds[messageObject.messageId]) {
              clearTimeout(messageTimeoutIds[messageObject.messageId]);
              delete messageTimeoutIds[messageObject.messageId];
            }
            const callback = messageErrorCallbacks[messageObject.messageId];
            delete messageErrorCallbacks[messageObject.messageId];
            try { callback(messageObject) } catch(e) {}
            messageHandled = true;
          }
        } catch(e) {}

        if (messageHandled) return;

        // Pass message to onMessage callback
        try {
          const data = JSON.parse(event.data);
          if(isArray(data)) { // if the data is an array assume each item is a separate action and let onMessage process them individually
            for(const message of data) {
              THIS.options.onMessage(message);
            }
          } else {
            THIS.options.onMessage(data);
          }
        } catch (error) {
          THIS.options.onError(new Error('Failed to parse message: ' + error.message));
        }
      };
    } catch (error) {
      THIS.options.onError(new Error('Failed to create WebSocket: ' + error.message));
      THIS.reconnect();
    }
  };

  THIS.send = function(message, onSuccess, onError) {
    try {
      const usingMessageCallback = typeof onSuccess === 'function' || typeof onError === 'function';
      if(usingMessageCallback && isObject(message)) {
        if(message.messageId) {
          debugLog('WebSocketUtility: Warning, overwriting your message\'s messageId attribute. This is a reserved attribute when using callbacks.');
        }
        message.messageId = WebSocketUtility.createMessageId();
        if(typeof onSuccess === 'function') {
          message.wantSuccessResponse = true;
          messageSuccessCallbacks[message.messageId] = onSuccess;
        }
        if(typeof onError === 'function') {
          message.wantErrorResponse = true;
          messageErrorCallbacks[message.messageId] = onError;
        }
      }
      if(usingMessageCallback && !isObject(message)) {
        debugLog('WebSocketUtility: Warning, callbacks are only supported when sending an Object.');
      }

      const stringifiedMessage = typeof message === 'string' ? message : JSON.stringify(message);
      
      // If the socket isn't connected queue messages to be sent after a connection occurs
      // Only allow non-login messaged to be sent after a successfull login message resolves. Queue non-login message to be sent after login occurs
      const isLoginMessage = isObject(message) && message.type === LOGIN;
      if (THIS.isConnected) {
        if(isAuthorized || isLoginMessage) {
          THIS.ws.send(stringifiedMessage);

          // Set error timeout function if the message is expecting a response and has provided an error function
          // Call the error handler if the timeout elapses without receiving a success response
          if(typeof onSuccess === 'function' && typeof onError === 'function') {
            messageTimeoutIds[message.messageId] = setTimeout(() => { onError({}); }, messageCallbackTimeout);
          }

          return true;
        } else {
          authorizedMessageQueue.push(stringifiedMessage);
          return false;
        }
      } else {
        if(isLoginMessage) {
          loginMessageQueue.push(stringifiedMessage);
          return false;
        } else {
          authorizedMessageQueue.push(stringifiedMessage);
          return false;
        }
      }
    } catch (error) {
      THIS.options.onError(new Error('Failed to send message: ' + error.message));
      return false;
    }
  };

  THIS.setAuthorized = function(newIsAuthorized) {
    isAuthorized = newIsAuthorized === true;
    while (isAuthorized && THIS.isConnected && authorizedMessageQueue.length > 0) {
      const message = authorizedMessageQueue.shift();
      THIS.send(message);
    }
  };

  THIS.processLoginMessageQueue = function() {
    while (loginMessageQueue.length > 0 && THIS.isConnected) {
      const message = loginMessageQueue.shift();
      THIS.send(message);
    }
  };

  THIS.reconnect = function(delayOverride) {
    if (THIS.reconnectTimer) {
      clearTimeout(THIS.reconnectTimer);
      THIS.reconnectTimer = null;
    }

    THIS.reconnectTimer = setTimeout(() => {
      THIS.connect();
    }, typeof delayOverride === 'number' && delayOverride >= 0 ? delayOverride : THIS.options.reconnectDelay);
  };

  THIS.disconnect = function() {
    THIS.autoReconnectEnabled = false; // the socket is being disconnected intentionally so don't attempt to reconnect

    if (THIS.ws) {
      THIS.ws.onopen = null;
      THIS.ws.onmessage = null;
      THIS.ws.onerror = null;
      THIS.ws.close();
      //THIS.ws.onclose = null;
      THIS.ws = null;
    } 

    if (THIS.reconnectTimer) {
      clearTimeout(THIS.reconnectTimer);
      THIS.reconnectTimer = null;
    }
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  THIS.hasPendingOptimisticUpdates = () => {
    for(const key in optimisticUpdateForId) {
      if(typeof optimisticUpdateForId[key].updateTimer === 'number' 
         || typeof optimisticUpdateForId[key].pendingServerDelete === 'function'
         || optimisticUpdateForId[key].unresolvedSentUpdates > 0 
         || optimisticUpdateForId[key].unresolvedSentDeletes > 0) { 
        return true;
      }
    }
    return false;
  };

  // Cancel sending an optimistic change to the server. Used for when an edit is pending and the item is deleted before the edit is sent to the server
  THIS.cancelPendingServerUpdatesForId = (itemId) => {
    if(itemId in optimisticUpdateForId && typeof optimisticUpdateForId[itemId].updateTimer === 'number') {
      clearTimeout(optimisticUpdateForId[itemId].updateTimer);
      optimisticUpdateForId[itemId].updateTimer = null;
      optimisticUpdateForId[itemId].unresolvedSentUpdates = 0;
      optimisticUpdateForId[itemId].unresolvedSentDeletes = 0;
      optimisticUpdateForId[itemId].rollbackItem = null;
      optimisticUpdateForId[itemId].pendingServerDelete = null;
    }
  };

  // Update an item locally now, but wait a preset amount of time without the user changing the item again to send the new item state to the server
  THIS.optimisticUpdateWithDebounce = (oldItem, newItem, localUpdateFunction, getItemFromSuccessResponseFunction, apiUpdateMessage, apiSuccessCallback, apiErrorCallback) => {
    // TODO: validate params
    // TODO: assert oldItem has id and that oldItem and newItem have the same id
    const itemId = oldItem.id;

    if(!(itemId in optimisticUpdateForId)) {
      optimisticUpdateForId[itemId] = {
        rollbackItem: oldItem,
        updateTimer: null,
        unresolvedSentUpdates: 0,
        unresolvedSentDeletes: 0,
        pendingServerDelete: null,
      };
      //debugLog('optimistic create');
      //debugLog('optimistic set rollback: ', oldItem);
    }
    const optimisticUpdate = optimisticUpdateForId[itemId];

    localUpdateFunction(newItem);
    //debugLog('optimistic localUpdate: ' + newItem);

    // cancel pending server update for this id if one exists 
    if(typeof optimisticUpdate.updateTimer === 'number') {
      clearTimeout(optimisticUpdate.updateTimer);
      optimisticUpdate.updateTimer = null;
    }

    // set a timeout to send update to the server
    optimisticUpdate.updateTimer = setTimeout(() => {
      clearTimeout(optimisticUpdate.updateTimer);
      optimisticUpdate.updateTimer = null;

      const onApiSuccess = (message) => {
        debugLog('server success updating item');
        optimisticUpdate.unresolvedSentUpdates -= 1;
        optimisticUpdate.rollbackItem = getItemFromSuccessResponseFunction(message);

        //debugLog('optimistic success2: ', getItemFromSuccessResponseFunction(message));
        //debugLog('optimistic set rollback: ', optimisticUpdate.rollbackItem);

        try { apiSuccessCallback(message); } catch(e) {}

        if(typeof optimisticUpdate.pendingServerDelete === 'function') {
          try { optimisticUpdate.pendingServerDelete(); } catch(e) {}
          optimisticUpdate.pendingServerDelete = null;
        }
      };
      const onApiError = (message) => {
        debugLog('server error updating item');
        optimisticUpdate.unresolvedSentUpdates -= 1;
        localUpdateFunction(optimisticUpdate.rollbackItem);
        //debugLog('optimistic error, rolling back to: ', optimisticUpdate.rollbackItem);

        if(typeof optimisticUpdate.pendingServerDelete === 'function') {
          try { optimisticUpdate.pendingServerDelete(); } catch(e) {}
          optimisticUpdate.pendingServerDelete = null;
        } else {
          try { apiErrorCallback(message); } catch(e) {}
        }
      };
      THIS.send(apiUpdateMessage, onApiSuccess, onApiError);
      optimisticUpdate.unresolvedSentUpdates += 1;
      //debugLog('optimistic sent: ', newItem);
    }, THIS.options.optimisticDebounceDelay);
  };

  THIS.optimisticDelete = (item, localDeleteFunction, localUndeleteFunction, apiUpdateMessage, apiSuccessCallback, apiErrorCallback) => {
    const itemId = item.id;
    let optimisticUpdate = optimisticUpdateForId[itemId];

    // delete the item locally without waiting
    localDeleteFunction(item);

    const sendServerDelete = () => {
      const onApiSuccess = (message) => {
        debugLog('server success deleting item');
        optimisticUpdate.unresolvedSentDeletes -= 1;
        try { apiSuccessCallback(message); } catch(e) {}
      };
      const onApiError = (message) => {
        debugLog('server error deleting item');
        optimisticUpdate.unresolvedSentDeletes -= 1;
        localUndeleteFunction(item);
        try { apiErrorCallback(message); } catch(e) {}
      };
      THIS.send(apiUpdateMessage, onApiSuccess, onApiError);
      optimisticUpdate.unresolvedSentDeletes += 1;
      debugLog('sent request for server to delete item');
    };

    if(isObject(optimisticUpdate)) {
      // cancel pending update
      if(typeof optimisticUpdate.updateTimer === 'number') {
        clearTimeout(optimisticUpdate.updateTimer);
        optimisticUpdate.updateTimer = null;
        debugLog('cancelled pending update');
      }
      optimisticUpdate.pendingServerDelete = null;

      if (optimisticUpdate.unresolvedSentDeletes <= 0) { // don't send or schedule a delete to be sent if one is currently being processed by the server
        if (optimisticUpdate.unresolvedSentUpdates > 0) {
          optimisticUpdate.pendingServerDelete = sendServerDelete;
          debugLog('scheduling delete for when update finishes');
        } else {
          sendServerDelete();
        }
      }
    } else {
      optimisticUpdateForId[itemId] = {
        rollbackItem: null,
        updateTimer: null,
        unresolvedSentUpdates: 0,
        unresolvedSentDeletes: 0,
        pendingServerDelete: null,
      };
      optimisticUpdate = optimisticUpdateForId[itemId];
      sendServerDelete();
    }
  };

  // Determine if the websocket is open but might be stale because the app has been suspended in the background
  // We can conclude that if we haven't successfully sent a heartbeat message in the last heartbeatDelay millis then the websocket could be stale
  THIS.isStale = () => {
    const millisElapsedSinceLastHeartbeatSent = new Date().getTime() - lastHeartbeatSentTimestamp;
    if(millisElapsedSinceLastHeartbeatSent > (THIS.options.heartbeatDelay + messageCallbackTimeout)) {
      debugLog('websocket stale, last heartbeat ' + millisElapsedSinceLastHeartbeatSent);
      return true;
    }
    return false;
  };

  // Initialize connection
  THIS.connect();
}

// Create a psuedo random unique id to track messages
WebSocketUtility.createMessageId = () => {
  let messageId = ''+new Date().getTime()+'-';
  for(let i = 0; i < 6; i++) {
    messageId += parseInt(Math.random()*9.99999);
  }
  return messageId;
};

export default WebSocketUtility;
